/**
 * ============================================================================
 * AUTH CONTEXT
 * Global authentication state management
 * ============================================================================
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Check if user is already logged in
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        // TODO: Validate token with backend
        // For now, assume token is valid and decode user info
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        setUser(userData);
        setToken(savedToken);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch('https://cca-coi-api.metamike.workers.dev/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        const { user: userData, token: authToken } = data;
        
        setUser(userData);
        setToken(authToken);
        
        localStorage.setItem('token', authToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        return userData;
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (error) {
      throw new Error(error.message || 'Login failed');
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch('https://cca-coi-api.metamike.workers.dev/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (data.success) {
        const { user: newUser, token: authToken } = data;
        
        setUser(newUser);
        setToken(authToken);
        
        localStorage.setItem('token', authToken);
        localStorage.setItem('user', JSON.stringify(newUser));
        
        return newUser;
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (error) {
      throw new Error(error.message || 'Registration failed');
    }
  };

  const logout = () => {
    // Clear React state
    setUser(null);
    setToken(null);
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refresh_token');
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear all cookies
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name) {
        // Clear for current domain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        // Clear for parent domain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        // Clear for subdomain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
      }
    }
    
    // Clear any cached API data
    if ('caches' in window) {
      caches.keys().then(function(cacheNames) {
        cacheNames.forEach(function(cacheName) {
          caches.delete(cacheName);
        });
      });
    }
    
    console.log('Logout: All authentication data cleared');
    
    // Redirect to login page
    window.location.href = '/login';
  };

  const updateProfile = async (profileData) => {
    try {
      // TODO: Replace with actual API call
      const response = await mockApiCall('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.success) {
        const updatedUser = { ...user, ...response.data };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      } else {
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error) {
      throw new Error(error.message || 'Profile update failed');
    }
  };

  // Mock API call function (replace with actual API implementation)
  const mockApiCall = async (endpoint, options) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Mock responses based on endpoint
    if (endpoint === '/auth/login') {
      const { email, password } = JSON.parse(options.body);
      
      // Mock validation
      if (email === 'admin@cca.com' && password === 'password') {
        return {
          success: true,
          data: {
            user: {
              id: 1,
              email: 'admin@cca.com',
              firstName: 'Admin',
              lastName: 'User',
              companyName: "Contractor's Choice Agency",
              role: 'admin'
            },
            token: 'mock-jwt-token-' + Date.now()
          }
        };
      } else {
        return {
          success: false,
          message: 'Invalid email or password'
        };
      }
    }

    if (endpoint === '/auth/register') {
      const userData = JSON.parse(options.body);
      return {
        success: true,
        data: {
          user: {
            id: Date.now(),
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            companyName: userData.companyName,
            phone: userData.phone,
            role: 'user'
          },
          token: 'mock-jwt-token-' + Date.now()
        }
      };
    }

    if (endpoint === '/auth/profile') {
      const profileData = JSON.parse(options.body);
      return {
        success: true,
        data: profileData
      };
    }

    // Default response
    return {
      success: false,
      message: 'Endpoint not found'
    };
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;